import { zodResolver } from '@hookform/resolvers/zod';
import { type Plan, PlanSchema } from '@projections/engine';
import type { Resolver } from 'react-hook-form';

/**
 * Plan EDITOR form wiring (PLAN.md M7 — "Validated with the SHARED engine Zod plan schema").
 *
 * The editor's form values ARE the engine `Plan` shape (one source of truth: the editor, the API,
 * the engine, and the test fixtures all share `PlanSchema`). react-hook-form validates against the
 * SAME schema via `@hookform/resolvers/zod`, so inline field errors are exactly the engine's own
 * validation messages — the editor and the simulator can never drift.
 *
 * Field-array sections (accounts, incomes, expenses, events, glidePaths) use `useFieldArray` keyed
 * by the array path; the XOR (allocation vs glidePathRef), the glidePathRef-resolves, and the
 * event-relative-TimePoint-resolves refinements all come from `PlanSchema`'s `.refine`/`.superRefine`
 * for free.
 */

/** The editor's form value type is the engine Plan itself. */
export type PlanFormValues = Plan;

/**
 * The react-hook-form resolver bound to the engine `PlanSchema`. Note: `PlanSchema` applies
 * defaults (e.g. `version`, `glidePaths: []`, stream `growth: 0`), so the resolved (output) values
 * are a fully-formed `Plan`. Use as `useForm({ resolver: planResolver, defaultValues })`.
 */
export const planResolver = zodResolver(PlanSchema) as Resolver<PlanFormValues>;

/** Validate a candidate plan object outside react-hook-form (e.g. on import/paste). */
export function validatePlan(input: unknown): ReturnType<typeof PlanSchema.safeParse> {
  return PlanSchema.safeParse(input);
}
